// window.Picker — the dropdown to reach for instead of a native <select>
// (10-16: Andres, on the Calling → Email campaigns pickers). A thin layer
// over window.Combobox (primitives.jsx), which already owns the portaled
// menu, search, keyboard, the phone bottom sheet and group headers; Picker
// adds the shape a <select> replacement wants:
//
//   value / onChange(value)   the plain value — not an event
//   options                   [{ value, label, hint?, count?, group?,
//                               description?, hue?, disabled? }] (strings ok)
//   emptyLabel                prepends { value: '', label } — "All industries"
//   multiple                  value is an array; rows toggle with a check and
//                             the menu stays open; the trigger summarizes
//                             ("Miami, Doral +2"); a Clear row when any picked
//   searchable                default: on above 6 options (Combobox's rule)
//   size                      'sm' (grid / toolbar height) | 'md'
//   width / minWidth / menuMinWidth / title / disabled / placeholder
//   creatable                 single only: typing a value that isn't listed
//                             offers "+ {createLabel(q)}" and picks the text
//                             itself (10-19: a new PhoneBurner tag)
//   autoOpen / onClose        mount with the menu open and hear when it
//                             closes — for a picker swapped in as an edit
//                             mode (Combobox's own props, single mode only)
//
// `count` renders muted at the row's right (1,811); `group` turns
// consecutive options into a headed section in the order given.

const _pickerNorm = (options) => {
  const out = [];
  let group;
  (Array.isArray(options) ? options : []).forEach((o) => {
    const opt = typeof o === 'string' || typeof o === 'number' ? { value: o, label: String(o) } : o;
    if (!opt) return;
    if (opt.group && opt.group !== group) {
      group = opt.group;
      out.push({ header: opt.group });
    }
    const count = opt.count != null && opt.count !== '' ? Number(opt.count).toLocaleString() : null;
    out.push({
      value: opt.value, label: opt.label != null ? String(opt.label) : String(opt.value),
      hint: [opt.hint, count].filter((x) => x != null && x !== '').join(' · ') || undefined,
      description: opt.description, hue: opt.hue, disabled: opt.disabled,
    });
  });
  return out;
};

const pickerSummary = (picked, labelOf, max = 2) => {
  if (!picked.length) return '';
  const names = picked.map(labelOf);
  return names.length <= max ? names.join(', ') : `${names.slice(0, max).join(', ')} +${names.length - max}`;
};

const Picker = ({
  value, onChange, options = [], placeholder = 'Select…', emptyLabel, multiple = false, searchable,
  size = 'md', width, minWidth, menuMinWidth, menuMaxHeight, title, disabled = false, style, footer,
  autoOpen, onClose, creatable = false, createLabel,
}) => {
  const norm = React.useMemo(() => {
    let base = _pickerNorm(options);
    // A typed (created) value shows on the trigger before it is a listed option.
    if (creatable && !multiple && value != null && value !== ''
        && !base.some((o) => o.header == null && String(o.value) === String(value))) {
      base = [{ value, label: String(value), hint: 'new' }, ...base];
    }
    return emptyLabel != null && !multiple ? [{ value: '', label: emptyLabel }, ...base] : base;
  }, [options, emptyLabel, multiple, creatable, value]);
  const createRow = ({ query, close }) => {
    const q = (query || '').trim();
    if (!q || norm.some((o) => o.header == null && String(o.label).toLowerCase() === q.toLowerCase())) return null;
    return (
      <button type="button" onClick={() => { onChange && onChange(q); close(); }}
        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none',
          background: 'transparent', color: 'var(--accent-text)', cursor: 'pointer', fontSize: 12.5 }}>
        + {createLabel ? createLabel(q) : `Use “${q}”`}
      </button>
    );
  };
  const box = { ...(width != null ? { width } : null), ...(minWidth != null ? { minWidth } : null), ...(style || {}) };

  if (!multiple) {
    return (
      <span title={title} style={{ display: 'inline-block', ...box }}>
        <window.Combobox
          value={value == null ? '' : value}
          onChange={(e) => onChange && onChange(e.value)}
          options={norm} placeholder={placeholder} searchable={searchable} searchAlways={creatable}
          disabled={disabled} dense={size === 'sm'} style={{ width: '100%' }}
          menuMinWidth={menuMinWidth} menuMaxHeight={menuMaxHeight} footer={footer || (creatable ? createRow : undefined)}
          autoOpen={autoOpen} onClose={onClose}
        />
      </span>
    );
  }

  const picked = Array.isArray(value) ? value : [];
  const labelOf = (v) => {
    const hit = norm.find((o) => o.header == null && String(o.value) === String(v));
    return hit ? hit.label : String(v);
  };
  const toggle = (v) => {
    const has = picked.some((x) => String(x) === String(v));
    onChange && onChange(has ? picked.filter((x) => String(x) !== String(v)) : [...picked, v]);
  };
  const clearRow = ({ close }) => (picked.length ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', fontSize: 12 }}>
      <span style={{ color: 'var(--text-3)', flex: 1 }}>{picked.length} picked</span>
      <button type="button" onClick={() => { onChange && onChange([]); close(); }}
        style={{ border: 'none', background: 'transparent', color: 'var(--accent-text)', cursor: 'pointer', fontSize: 12, padding: 0 }}>
        Clear
      </button>
    </div>
  ) : null);
  return (
    <span title={title || (picked.length > 2 ? picked.map(labelOf).join(', ') : undefined)}
      style={{ display: 'inline-block', ...box }}>
      <window.Combobox
        value="" onChange={(e) => toggle(e.value)} keepOpen
        selectedValues={picked} display={pickerSummary(picked, labelOf)}
        options={norm} placeholder={placeholder} searchable={searchable}
        disabled={disabled} dense={size === 'sm'} style={{ width: '100%' }}
        menuMinWidth={menuMinWidth} menuMaxHeight={menuMaxHeight}
        footer={footer || clearRow}
      />
    </span>
  );
};

Object.assign(window, { Picker, pickerSummary });
